import type { FloorIndex } from './types.js';
import type { LiftParts } from './upgrades.js';

/**
 * What a building *is*, mechanically.
 *
 * The product vision names one anti-pillar that this milestone exists to avoid:
 * "new buildings that are only reskins". So a building is not a backdrop with a
 * name — it is a small bundle of structural rules, in exactly the way an
 * archetype is a small bundle of passenger traits. If a building can be
 * described entirely by how it looks, it is not a building.
 *
 * Three rules, each attacking a different part of the traffic problem, and each
 * chosen because v0.4 measured where the problem actually lives (ADR-0013: what
 * makes a level is the choice of *whom to collect*):
 *
 *   landingCapacity — changes WHEN you must collect. A floor that cannot hold a
 *                     queue turns "I will come back for them" into a loss.
 *   entrances       — changes WHERE demand starts. v0.4 found concentrated
 *                     origins flatten a level; a second entrance un-flattens it
 *                     without touching the traffic pattern at all.
 *   interchange     — changes WHAT a delivery IS. A journey across the boundary
 *                     becomes two journeys, and the transfer floor becomes a
 *                     congestion point the player creates for themselves.
 *
 * Adding a fourth building must cost a JSON file. If it costs a code path, the
 * rule was written for one building instead of for the game.
 */

export interface Interchange {
  /** Everyone crossing this line changes lifts. */
  readonly boundary: FloorIndex;
  /** Where they change. Must be reachable from both zones. */
  readonly floor: FloorIndex;
}

export interface BuildingRules {
  /**
   * How many people can stand waiting on a floor that is not an entrance.
   * Someone arriving at a full landing turns away immediately rather than
   * queueing.
   *
   * Deliberately harsh: the alternative — letting the queue grow and charging
   * for it later — is a percentage penalty wearing a costume, and the player
   * cannot see it happen. Turning away is visible in the moment it happens.
   *
   * Entrances are exempt, and that exemption is what makes the rule playable
   * rather than merely punishing. A hotel lobby is built to hold a crowd; its
   * upstairs corridors are not. Applied to entrances as well, any pattern that
   * starts everyone on the ground floor turns into arithmetic instead of a
   * decision: arrivals outrun any possible round trip and most of the level is
   * lost before the lift has moved twice. Measured, not assumed — the first
   * version of `31-narrow-corridors` delivered nobody at all, with every bot.
   */
  readonly landingCapacity?: number;
  /** Floors where outside traffic enters the building. Defaults to the lobby. */
  readonly entrances?: readonly FloorIndex[];
  /** A floor every cross-boundary journey must pass through. */
  readonly interchange?: Interchange;
}

export interface BuildingDef {
  readonly id: string;
  readonly name: string;
  /** What this building does to the traffic problem, in one sentence. */
  readonly premise: string;
  readonly rules: BuildingRules;
  /**
   * The building's own lift (ADR-0027), from its stock car to the best it can
   * be fitted with. Absent means the standard lift (`UPGRADES`).
   */
  readonly lift?: LiftParts;
  /** What a shift here pays, by stars (ADR-0027). Absent means `PAYOUT_BY_STARS`. */
  readonly payout?: Readonly<Record<number, number>>;
  /**
   * Which floor is at street level (ADR-0028); the floors below it are
   * underground and named −1, −2… Naming and drawing only: no rule reads it,
   * so moving it changes no level and no replay. Absent means floor 0.
   */
  readonly groundFloor?: FloorIndex;
}

/** Floors where the building expects a crowd, and the landing limit does not apply. */
export function entrancesOf(rules: BuildingRules): readonly FloorIndex[] {
  return rules.entrances && rules.entrances.length > 0 ? rules.entrances : [0];
}

/** No structural rules at all — the tower levels 1-30 were built in. */
export const PLAIN_BUILDING: BuildingRules = {};

/**
 * Does this journey cross the interchange boundary?
 *
 * Both endpoints on the same side means the lift can simply take them; a
 * crossing means the trip is two legs whether the player likes it or not.
 */
export function crossesBoundary(
  rules: BuildingRules,
  origin: FloorIndex,
  destination: FloorIndex,
): boolean {
  const ic = rules.interchange;
  if (!ic) return false;
  return origin <= ic.boundary !== destination <= ic.boundary;
}
